import React, { useCallback, useEffect, useState } from 'react';
import { useLocalizedText } from '../../hooks/localizedText';
import { useErrorMessages, WAITLIST_LOAD_FAILED_KEY } from '../../hooks/localization/errorMessages';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import {
  AppLayout,
  BreadcrumbGroup,
  Button,
  ContentLayout,
  Flashbar,
  FlashbarProps,
  Header,
  Link,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { BulkLeaveWaitlistCommandOutput } from '@amzn/red-velvet-api';
import { ActivitySidebar } from '../myActivity/sideNav';
import { WaitlistInfoHelpPanel } from '../myActivity/waitlistInfoHelpPanel';
import { getValueFromSearchParam } from '../../utils/url';
import { useGetAllWaitlistsByEvent } from '../../hooks/waitlist/useGetAllWaitlistsByEvent';
import { isDisplayableWaitlistEntry } from '../../utils/waitlistUtils';
import { WaitlistEventCard } from './waitlistEventCard';
import { WaitlistTable } from './waitlistTable';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import { NAVIGATION_REDESIGN, SHIFT_WAITLIST } from '../../utils/constants';
import { isUnlistedEvent } from '../../utils/eventUtils';
import { publishKatalMetric } from '../katalAnalytics';
import { COORDINATOR_WAITLIST_PAGE_VIEW } from '../../utils/constants';
import { AppOutletContext } from '../../App';

export function CoordinatorManageWaitlistPage() {
  const { t } = useLocalizedText(['coordinatorManageWaitlist', 'activity', 'translation']);
  const { resolveError } = useErrorMessages();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlFeatures = searchParams.get('features');
  const features = useFeatures();
  const eventId = getValueFromSearchParam(searchParams, 'eventId', '');
  const isWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);
  const { data, isError, error, refetch, isLoading } = useGetAllWaitlistsByEvent(eventId);
  const event = data?.event;
  const displayEntries = (data?.waitlistEntries ?? []).filter(isDisplayableWaitlistEntry);
  const coordinatorActivityUrl = `/coordinator-activity${urlFeatures ? '?features=' + urlFeatures : ''}`;

  const [flashItems, setFlashItems] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);

  const isNewNav = featureIsEnabled(NAVIGATION_REDESIGN, features, searchParams);
  const outletContext = useOutletContext<AppOutletContext | null>();

  const toolsPanel = <WaitlistInfoHelpPanel />;

  // New nav: the root AppLayout owns the tools slot. Publish the help panel on mount so the
  // info icon is available, and clean it up on unmount. In legacy the panel lives in this
  // page's own AppLayout via the infoPanelOpen state below.
  useEffect(() => {
    if (isNewNav && outletContext) {
      outletContext.setTools(toolsPanel);
    }
    return () => {
      if (isNewNav && outletContext) {
        outletContext.setTools(null);
        outletContext.setToolsOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav]);

  // Open the waitlist info help panel. In new nav the panel is owned by the root AppLayout
  // (opened via outletContext); in legacy it is local to this page's AppLayout.
  const openInfoPanel = () => {
    if (isNewNav && outletContext) {
      outletContext.setToolsOpen(true);
    } else {
      setInfoPanelOpen(true);
    }
  };

  // Gate the page-view on the feature flag: React runs effects even when the component renders
  // null (flag off), so without this guard navigating here with the flag off would still inflate
  // the metric during limited rollout.
  useEffect(() => {
    if (isWaitlistEnabled) {
      publishKatalMetric(COORDINATOR_WAITLIST_PAGE_VIEW, `eventId=${eventId}`);
    }
  }, [eventId, isWaitlistEnabled]);

  // New nav: publish bulk-remove results into the root AppLayout's notifications region. The
  // shared layout owns the only notifications slot, so pages push into it rather than rendering a
  // Flashbar inside their content (CR-291797692 rev 8). Keyed on flashItems (the data) so the
  // effect doesn't re-run every render; cleared on unmount so results don't leak to the next page.
  useEffect(() => {
    if (!isNewNav || !outletContext) return;
    outletContext.setNotifications(
      flashItems.length > 0 ? <Flashbar data-testid="waitlist-flashbar" items={flashItems} /> : null
    );
    return () => outletContext.setNotifications(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNewNav, flashItems]);

  const dismissFlashItem = useCallback(
    (id: string) => setFlashItems((prev) => prev.filter((item) => item.id !== id)),
    []
  );

  const handleRemoveComplete = useCallback(
    (result: BulkLeaveWaitlistCommandOutput) => {
      const items: FlashbarProps.MessageDefinition[] = [];
      const succeededCount = result.succeededDeletedWaitlistEntries?.length ?? 0;
      const failed = result.failedDeletedWaitlistEntries ?? [];

      if (succeededCount > 0) {
        items.push({
          type: 'success',
          dismissible: true,
          onDismiss: () => dismissFlashItem('bulk-remove-success'),
          content: t('bulkRemoveAlerts.successMessage', { count: succeededCount }),
          id: 'bulk-remove-success',
        });
      }

      if (failed.length > 0) {
        const aliasById = new Map(
          (data?.waitlistEntries ?? []).map((entry) => [entry.waitlistEntryId, entry.alias])
        );
        const aliases = failed
          .map((f) => aliasById.get(f.waitlistEntryId))
          .filter(Boolean)
          .join(', ');
        items.push({
          type: 'error',
          dismissible: true,
          onDismiss: () => dismissFlashItem('bulk-remove-failure'),
          content: t('bulkRemoveAlerts.failureMessage', { count: failed.length }),
          action: (
            <Button onClick={() => navigator.clipboard?.writeText(aliases)?.catch(() => undefined)}>
              {t('bulkRemoveAlerts.copyFailedAliases')}
            </Button>
          ),
          id: 'bulk-remove-failure',
        });
      }

      setFlashItems(items);
      refetch();
    },
    [t, data?.waitlistEntries, dismissFlashItem, refetch]
  );

  if (!isWaitlistEnabled) {
    return null;
  }
  function renderWaitlistTableContent() {
    if (isError) {
      return (
        <Flashbar
          items={[
            {
              type: 'error',
              header: t('apiError.header'),
              content: resolveError(error, WAITLIST_LOAD_FAILED_KEY),
            },
          ]}
        />
      );
    }

    if (event && isUnlistedEvent(event)) {
      return (
        <Flashbar
          items={[
            {
              type: 'warning',
              content: t('unsupportedEvent.content'),
            },
          ]}
        />
      );
    }

    return (
      <WaitlistTable
        entries={displayEntries}
        shifts={data?.shifts ?? []}
        isLoading={isLoading}
        onBulkRemoveComplete={handleRemoveComplete}
      />
    );
  }

  const notifications =
    flashItems.length > 0 ? (
      <Flashbar data-testid="waitlist-flashbar" items={flashItems} />
    ) : undefined;

  const content = (
    <ContentLayout
      header={
        event && (
          <Header
            variant="h1"
            info={
              <Link
                variant="info"
                data-testid="manageWaitlist.infoLink"
                data-aci-analytics-name="Waitlist_infoPanelOpened"
                onFollow={(e) => {
                  e.preventDefault();
                  openInfoPanel();
                }}
              >
                {t('waitlistsTab.info.linkLabel', { ns: 'activity', defaultValue: 'Info' })}
              </Link>
            }
          >
            {t('header.title', { eventTitle: event.title })}
          </Header>
        )
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {event && <WaitlistEventCard event={event} />}
        {renderWaitlistTableContent()}
      </SpaceBetween>
    </ContentLayout>
  );

  // New navigation: the root AppLayout in App.tsx owns the side nav, breadcrumbs (via
  // SiteBreadcrumbs), tools panel, and notifications region (all synced through outletContext
  // above), so render content only.
  if (isNewNav) {
    return content;
  }

  // Legacy: this page owns its own AppLayout shell.
  return (
    <AppLayout
      footerSelector=".footer"
      navigation={<ActivitySidebar baseUrl={coordinatorActivityUrl} />}
      toolsHide={false}
      toolsOpen={infoPanelOpen}
      onToolsChange={({ detail }) => setInfoPanelOpen(detail.open)}
      tools={toolsPanel}
      contentType="table"
      notifications={notifications}
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            {
              text: t('breadcrumbs.home', { ns: 'translation' }),
              href: `/${urlFeatures ? '?features=' + urlFeatures : ''}`,
            },
            {
              text: t('breadcrumbs.coordinatorActivity', { ns: 'activity' }),
              href: coordinatorActivityUrl,
            },
            { text: t('breadcrumbs.manageWaitlist'), href: '#' },
          ]}
          onFollow={(e) => {
            e.preventDefault();
            navigate(e.detail.href);
          }}
        />
      }
      content={content}
    />
  );
}
